import { useQuery } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/auth-context';
import {
  useAllianceIdentity,
  useAuthorizeQuery,
} from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { useAppAccessRefused } from '../utils/app-access';
import { getAuthorizeErrorData, getErrorStatus } from '../utils/authorize';
import { captureError } from '../utils/capture-error';
import { checkForRedirectPath, storeRedirectPath } from '../utils/redirect';
import { formatAllianceDate } from './alliance/app-banners';
import { SystemIdentity, SystemSplash } from './alliance/system-screen';
import {
  AccessDeniedScreen,
  AccountInactiveScreen,
  AccountPendingScreen,
  AppNoAccessScreen,
  AuthFailScreen,
  MaintenanceScreen,
  MfaRequiredScreen,
  NotAuthorizedScreen,
  SessionExpiredScreen,
} from './alliance/system-screens';

/**
 * True when an error means the Cognito session is gone (refresh token
 * expired or revoked). Amplify sometimes rejects with a plain string.
 */
const isSessionError = (error: unknown) => {
  const err = error as {
    code?: string;
    name?: string;
    message?: string;
    response?: { status?: number };
  } | null;
  if (err?.response?.status === 401) {
    return true;
  }
  const text = `${err?.code || ''} ${err?.name || ''} ${
    err?.message || (typeof error === 'string' ? error : '')
  }`;
  return /not authenticated|no current user|NotAuthorizedException|refresh token|token has expired|token has been revoked/i.test(
    text,
  );
};

/**
 * The gate in front of the signed-in shell. In order:
 *
 * 1. Signing out: hold the splash until Cognito is done
 * 2. Maintenance (agents only; admins get the banner in the shell)
 * 3. Splash while the session, MFA and the Contracting /authorize load
 *    ("Signing you in…" on the return from Cognito)
 * 4. Session expired, when Cognito says the session is gone. Checked before
 *    MFA, so a dead session never lands on the MFA screen
 * 5. The MFA check failed for another reason: couldn't load (not "no MFA")
 * 6. MFA not set up: the MFA screen, which sends them to Contracting
 * 7. Contracting /authorize 403: UserStatus Pending / Inactive / Terminated
 *    → that screen, any other 403 → not authorized. Any other /authorize
 *    failure doesn't block (the bar falls back to the token)
 * 8. An inactive employee record (/authorize Staff.Active '0')
 * 9. The University API refused every call so far (403, nothing allowed):
 *    "No access to Alliance University" (non-admins)
 * 10. The app
 */
export function RequireAuth(props: {
  children: JSX.Element;
  isAdmin: boolean;
  maintData:
    | {
        maintenanceActive: boolean;
        maintenanceBegin: string;
        maintenanceEnd: string;
      }
    | null
    | undefined;
}) {
  // Context
  const { state, dispatch } = React.useContext(AuthContext);
  // State
  const [fromCognito] = React.useState(() =>
    new URLSearchParams(window.location.search).has('code'),
  );
  // Hooks
  const navigate = useNavigate();
  const identity = useAllianceIdentity();
  const queryAuthorize = useAuthorizeQuery();
  const appRefused = useAppAccessRefused();
  const { signOut, signInAgain } = useSessionActions();

  React.useEffect(() => {
    const handleCheckForPath = async () => {
      try {
        const redirectPath = await checkForRedirectPath();
        if (redirectPath) {
          navigate(redirectPath);
        }
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    const handleStorePath = async () => {
      try {
        if (window?.location?.pathname) {
          const { pathname } = window.location;
          // If the user was trying to access a route other than the main route
          if (pathname !== '/') {
            let path = pathname;
            // If there are any search parameters add them to the redirect path
            if (window?.location?.search) {
              path = path + window.location.search;
            }
            // Add the route to storage for use after logging in
            await storeRedirectPath({ pathname: path });
          }
        }
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    if (state.user !== undefined) {
      // Check for stored path
      handleCheckForPath();
    } else {
      // Store path
      handleStorePath();
    }
  }, [state.user, navigate, dispatch]);

  const isAuthenticated = state.user !== undefined;

  // Query - Check that the user has set up Multi-Factor Authentication for their account
  const queryMFA = useQuery({
    enabled: isAuthenticated,
    queryKey: ['check_Auth.getPreferredMFA'],
    queryFn: async () => {
      // Bypass Cache to always get the latest preferences for the Current User
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      const preferredMFA = await Auth.getPreferredMFA(currentUser);

      const mfaOptions = ['EMAIL_OTP', 'SMS_MFA', 'SOFTWARE_TOKEN_MFA'];

      // Determine if the user has selected one of the available options
      return mfaOptions.includes(preferredMFA || '');
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // We need to check the fetch status to see if the query is actually running
  const isLoadingMFA = queryMFA.isLoading && queryMFA.fetchStatus !== 'idle';
  const isAuthorizing =
    queryAuthorize.isLoading && queryAuthorize.fetchStatus !== 'idle';

  // "Signed in as" on the full-page screens: name + number once /authorize
  // has answered, otherwise the Cognito username
  // A 403 can still carry the agent's record (and its UserStatus)
  const authorizeErrorData = getAuthorizeErrorData({
    error: queryAuthorize.error,
  });
  const forbiddenAgent = authorizeErrorData?.Agent;
  const forbiddenName = [forbiddenAgent?.Fname, forbiddenAgent?.Lname]
    .filter(Boolean)
    .join(' ');

  let systemIdentity: SystemIdentity | null = null;
  if (identity && queryAuthorize.isSuccess) {
    systemIdentity = {
      kind: 'full',
      name: identity.user.fullName,
      id: identity.user.id,
    };
  } else if (forbiddenAgent?.AgtNo && forbiddenName) {
    systemIdentity = {
      kind: 'full',
      name: forbiddenName,
      id: forbiddenAgent.AgtNo,
    };
  } else if (state.user) {
    systemIdentity = { kind: 'token', id: state.user.getUsername() };
  }

  const authorizeStatus = queryAuthorize.isError
    ? getErrorStatus({ error: queryAuthorize.error })
    : undefined;
  const isForbidden = authorizeStatus === 403;
  const userStatus = authorizeErrorData?.UserStatus;

  const sessionExpired =
    (queryMFA.isError && isSessionError(queryMFA.error)) ||
    (queryAuthorize.isError && isSessionError(queryAuthorize.error)) ||
    (state.tokenError && (queryAuthorize.isError || queryMFA.isError));

  let screen: JSX.Element | null = null;
  if (isAuthenticated && state.signingOut) {
    // Hold here until Cognito has signed out (then USER_CLEAR → Sign In)
    screen = <SystemSplash caption="Signing you out…" />;
  } else if (
    !props.isAdmin &&
    props.maintData &&
    props.maintData.maintenanceActive
  ) {
    // Display a Maintenance Message to agents
    screen = (
      <MaintenanceScreen
        until={formatAllianceDate({
          value: props.maintData.maintenanceEnd,
          long: true,
        })}
        identity={systemIdentity}
        onSignOut={isAuthenticated ? signOut : undefined}
      />
    );
  } else if (state.isChecking || isLoadingMFA || isAuthorizing) {
    screen = (
      <SystemSplash caption={fromCognito ? 'Signing you in…' : undefined} />
    );
  } else if (isAuthenticated && sessionExpired) {
    screen = <SessionExpiredScreen onSignIn={signInAgain} />;
  } else if (isAuthenticated && queryMFA.isError) {
    // The MFA check failed for another reason: that isn't "no MFA"
    screen = (
      <AuthFailScreen
        identity={systemIdentity}
        onRetry={() => queryMFA.refetch()}
        onSignOut={signOut}
      />
    );
  } else if (isAuthenticated && queryMFA.isSuccess && !queryMFA.data) {
    screen = (
      <MfaRequiredScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isForbidden && userStatus === 'Pending') {
    screen = (
      <AccountPendingScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isForbidden && userStatus === 'Inactive') {
    screen = (
      <AccountInactiveScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isForbidden && userStatus?.startsWith('Terminated')) {
    screen = <AccessDeniedScreen onSignOut={signOut} />;
  } else if (isForbidden) {
    screen = (
      <NotAuthorizedScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (queryAuthorize.data?.Staff?.Active === '0') {
    // An inactive employee (admin) record
    screen = (
      <AccountInactiveScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isAuthenticated && appRefused && !props.isAdmin) {
    screen = (
      <AppNoAccessScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isAuthenticated) {
    return props.children;
  } else {
    return <Navigate to="/sign-in" replace />;
  }

  return screen;
}
